import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import {
  RouteNavigationData,
  RouteNavigationEvent,
  RouteNavigationService,
  RouteQueryParams,
} from '../../services/route-navigation.service';
import {
  NavigationConfig,
  NavigationEvent,
} from '../navigation-link/interfaces/navigation.interfaces';
import { NavigationLinkComponent } from '../navigation-link/navigation-link.component';

export type RouteDisplayMode = 'code' | 'name' | 'both';

@Component({
  selector: 'app-route-navigation',
  standalone: true,
  imports: [CommonModule, NavigationLinkComponent],
  templateUrl: './route-navigation.component.html',
})
export class RouteNavigationComponent {
  private readonly routeNavService = inject(RouteNavigationService);

  readonly route = input<RouteNavigationData | null | undefined>();
  readonly displayMode = input<RouteDisplayMode>('code');
  readonly customQueryParams = input<RouteQueryParams>({});
  readonly openInNewTab = input<boolean>(false);
  readonly customClass = input<string>('');
  readonly showIcon = input<boolean>(true);
  readonly iconPosition = input<'left' | 'right'>('right');
  readonly icon = input<string>('heroicons_outline:rocket-launch');
  readonly showText = input<boolean>(true);
  readonly interactionMode = input<'both' | 'text-only' | 'icon-only'>('both');
  readonly customTooltipText = input<string | undefined>();

  readonly routeNavigate = output<RouteNavigationEvent>();

  readonly isNavigable = computed(() => this.routeNavService.isRouteNavigable(this.route()));

  readonly displayText = computed(() => {
    const r = this.route();
    if (!r) return '-';

    const code = r.externalKeys?.code || r.code;
    const name = r.name;

    switch (this.displayMode()) {
      case 'code':
        return code || name || (r.id != null ? String(r.id) : '-');
      case 'name':
        return name || code || '-';
      case 'both':
        if (code && name) return `${code} - ${name}`;
        return code || name || '-';
      default:
        return code || '-';
    }
  });

  readonly tooltipText = computed(() => {
    if (!this.isNavigable()) return '';
    return this.customTooltipText() || this.routeNavService.getNavigationDescription();
  });
  readonly displayCode = computed(() => {
    const d = this.route();
    if (!d) return '';
    return (d as any).externalKeys?.code || (d as any).code || '';
  });

  readonly displayName = computed(() => {
    return (this.route() as any)?.name || '';
  });

  readonly navigationConfig = computed<NavigationConfig<RouteNavigationData, RouteQueryParams>>(
    () => ({
      data: this.route() ?? undefined,
      url: ['master', 'routing', 'route'],
      queryParams: this.customQueryParams(),
      openInNewTab: this.openInNewTab(),
    })
  );

  handleNavigation(navigationEvent: NavigationEvent<RouteNavigationData, RouteQueryParams>): void {
    if (!this.isNavigable()) return;

    const routeNavigationEvent: RouteNavigationEvent = {
      data: navigationEvent.config.data,
      queryParams: navigationEvent.config.queryParams || {},
      timestamp: navigationEvent.timestamp,
      source: 'general',
    };

    this.routeNavigate.emit(routeNavigationEvent);

    this.routeNavService.navigateToRouteDetail(
      navigationEvent.config.data,
      navigationEvent.config.queryParams,
      navigationEvent.config.openInNewTab
    );
  }
}
